import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface SectionVisibility {
  id: string;
  section_key: string;
  section_name: string;
  is_visible: boolean;
  display_order: number;
}

export const useSectionVisibility = () => {
  const queryClient = useQueryClient();

  const { data: sections, isLoading } = useQuery({
    queryKey: ["section-visibility"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("section_visibility")
        .select("*")
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as SectionVisibility[];
    },
  });

  const toggleVisibility = useMutation({
    mutationFn: async ({ sectionKey, isVisible }: { sectionKey: string; isVisible: boolean }) => {
      const { error } = await supabase
        .from("section_visibility")
        .update({ is_visible: isVisible })
        .eq("section_key", sectionKey);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["section-visibility"] });
    },
  });

  const isSectionVisible = (sectionKey: string): boolean => {
    if (!sections) return true; // Default to visible if not loaded
    const section = sections.find((s) => s.section_key === sectionKey);
    return section?.is_visible ?? true;
  };

  return {
    sections,
    isLoading,
    isSectionVisible,
    toggleVisibility,
  };
};
